import { useMemo, useState } from "react";
import { Check, Trash2 } from "lucide-react";
import { excluirRealizadoDepara, getRealizadoDepara, salvarRealizadoDepara } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import {
  AvisoErro, Badge, BarraFiltros, Card, Carregando, Seletor, Vazio, estiloTabela, formatValor, inputStyle, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// De-para de projetos: nome digitado na planilha/formulário → projeto do cadastro da Gestão
// Orçamentária (o mesmo usado no orçado). Salvar reaponta na hora todas as solicitações e
// competências com aquele nome.

function LinhaDepara({ C, T, linha, projetos, onSalvo, onErro }) {
  const [projetoId, setProjetoId] = useState(linha.projetoId ? String(linha.projetoId) : "");
  const [salvando, setSalvando] = useState(false);
  const mudou = projetoId !== (linha.projetoId ? String(linha.projetoId) : "");

  const salvar = async () => {
    setSalvando(true);
    try {
      if (projetoId) await salvarRealizadoDepara(linha.alias, Number(projetoId));
      else await excluirRealizadoDepara(linha.alias);
      onSalvo();
    } catch (e) {
      onErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <tr>
      <td style={{ ...T.td, maxWidth: 280 }}>
        <div style={{ fontWeight: 600 }}>{linha.alias}</div>
        {linha.textos.length > 1 && <div style={{ color: C.muted, fontSize: 11 }}>{linha.textos.join(" · ")}</div>}
      </td>
      <td style={{ ...T.td, ...T.tdNum }}>{linha.solicitacoes || "—"}</td>
      <td style={{ ...T.td, ...T.tdNum }}>{linha.competencias || "—"}</td>
      <td style={{ ...T.td, ...T.tdNum }}>{formatValor(linha.valor)}</td>
      <td style={{ ...T.td, minWidth: 260 }}>
        <select value={projetoId} onChange={(e) => setProjetoId(e.target.value)} style={{ ...inputStyle(C), minHeight: 32, padding: "5px 8px", fontSize: 12 }}>
          <option value="">— sem de-para —</option>
          {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}{p.ativo ? "" : " (inativo)"}</option>)}
        </select>
      </td>
      <td style={{ ...T.td, whiteSpace: "nowrap" }}>
        {mudou ? (
          <button onClick={salvar} disabled={salvando} title="Salvar" style={{ background: C.brand, color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            {projetoId ? <Check size={13} /> : <Trash2 size={13} />} {salvando ? "…" : projetoId ? "Salvar" : "Remover"}
          </button>
        ) : linha.projetoId ? (
          <Badge cor={C.success}>{linha.criadoPor === "AUTOMATICO" ? "Automático" : "Mapeado"}</Badge>
        ) : (
          <Badge cor={C.warning}>Pendente</Badge>
        )}
      </td>
    </tr>
  );
}

export default function DeparaProjetosRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const [filtro, setFiltro] = useState("pendentes");
  const [erroAcao, setErroAcao] = useState("");
  const { dados, carregando, erro, recarregar } = useCarregar(() => getRealizadoDepara(), []);
  const T = estiloTabela(C);

  const linhas = useMemo(() => (dados?.linhas || []).filter((l) => filtro === "todos" || !l.projetoId), [dados, filtro]);
  const pendentes = (dados?.linhas || []).filter((l) => !l.projetoId);

  return (
    <RealizadoLayout titulo="De-para de projetos" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErroAcao("")}>{erro || erroAcao}</AvisoErro>
      {carregando && !dados ? <Carregando C={C} /> : dados && (
        <>
          <Card
            C={C}
            titulo="Nome na planilha → projeto do cadastro"
            subtitulo={`${pendentes.length} nome(s) sem de-para, somando R$ ${formatValor(pendentes.reduce((a, l) => a + l.valor, 0))}. Nomes que batem exatamente com o cadastro são mapeados automaticamente.`}
            acoes={
              <BarraFiltros>
                <Seletor C={C} rotulo="Mostrar" value={filtro} onChange={setFiltro} opcoes={[
                  { value: "pendentes", label: "Só pendentes" }, { value: "todos", label: "Todos" },
                ]} />
              </BarraFiltros>
            }
          >
            {linhas.length === 0 ? <Vazio C={C}>Nenhum nome pendente. 🎉</Vazio> : (
              <div style={{ ...T.wrap, maxHeight: "55vh" }}>
                <table style={{ ...T.table, minWidth: 900 }}>
                  <thead><tr>
                    <th style={T.th}>Nome de origem (normalizado)</th>
                    <th style={{ ...T.th, ...T.thNum }}>Solicitações</th>
                    <th style={{ ...T.th, ...T.thNum }}>Competências</th>
                    <th style={{ ...T.th, ...T.thNum }}>Valor</th>
                    <th style={T.th}>Projeto do cadastro</th>
                    <th style={T.th} />
                  </tr></thead>
                  <tbody>
                    {linhas.map((l) => (
                      <LinhaDepara key={`${l.alias}-${l.projetoId}`} C={C} T={T} linha={l} projetos={dados.projetos} onSalvo={recarregar} onErro={setErroAcao} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
              “(VAZIO)” agrupa as solicitações em que o nome do projeto veio vazio ou “.” na planilha — não dá para mapear por nome.
              Projeto que não existe no cadastro deve ser criado antes em Gestão Orçamentária › Custos Gerais por Projeto.
            </div>
          </Card>
        </>
      )}
    </RealizadoLayout>
  );
}
